import { requestResetPasswordApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { recoverSchema } from "@/features/auth/schema/recoverSchema";
import { TRecoverForm } from "@/features/auth/types/recoverForm";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

// Cooldown period: 3 seconds between submissions
const SUBMISSION_COOLDOWN_MS = 3000;

export const RecoverForm = () => {
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);
  const lastSubmitTimeRef = useRef<number>(0);
  const [isCooldown, setIsCooldown] = useState(false);
  const methods = useForm<TRecoverForm>({
    mode: "onChange",
    resolver: yupResolver(recoverSchema),
  });
  const { control, formState } = methods;

  const onSubmit = async (data: TRecoverForm) => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTimeRef.current;

    // Prevent double submission
    if (isSubmittingRef.current) {
      return;
    }

    // Prevent rapid clicks (cooldown period)
    if (timeSinceLastSubmit < SUBMISSION_COOLDOWN_MS) {
      const remainingTime = Math.ceil((SUBMISSION_COOLDOWN_MS - timeSinceLastSubmit) / 1000);
      showError(new Error(`送信後、${remainingTime}秒待ってから再度お試しください。`));
      return;
    }

    isSubmittingRef.current = true;
    lastSubmitTimeRef.current = now;
    setIsCooldown(true);

    try {
      await requestResetPasswordApi(data);
      navigate(
        getDynamicRoute(
          AppRoutes.recoverComplete,
          {},
          `email=${encodeURIComponent(data.email)}&ownerId=${encodeURIComponent(data.ownerId)}`,
        ),
      );
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
      isSubmittingRef.current = false; // Reset on error to allow retry
      setIsCooldown(false);
      
      // Reset cooldown after error, but still enforce minimum delay
      setTimeout(() => {
        lastSubmitTimeRef.current = 0;
      }, SUBMISSION_COOLDOWN_MS);
    }
  };

  const isDisabled = formState.isSubmitting || isSubmittingRef.current || isCooldown;

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={recoverSchema}>
      <Stack gap={3} width={400}>
        <FormField control={control} name="ownerId">
          <Input size="lg" placeholder="入力してください" disabled={isDisabled} />
        </FormField>
        <FormField control={control} name="email">
          <Input size="lg" placeholder="example@irohana.co.jp" disabled={isDisabled} />
        </FormField>
        <Button type="submit" variant="primary" fullWidth disabled={isDisabled}>
          送信
        </Button>
      </Stack>
    </Form>
  );
};
